import React, { useState } from "react";
import {
  FormControl,
  Select,
  MenuItem,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  SelectChangeEvent,
  Button,
  TextField,
  Tooltip,
} from "@mui/material";
import { AutoAwesome } from "@mui/icons-material";
import useStore from "../../../Store/Store";
import { theme } from "../../../Themes/Themes";
import { useIsBelow } from "../../../utils/responsive";

// Define initial model options array
const initialModelOptions = [
  {
    value: "gemini-2.5-flash-preview-05-20",
    label: "gemini-2.5-flash-preview-05-20",
  },
  {
    value: "gemini-2.5-pro",
    label: "gemini-2.5-pro",
  },
];

// Helper function to get model display info
const getModelDisplayInfo = (modelValue: string) => {
  const modelMap: Record<
    string,
    { displayName: string; showMax?: boolean; hasGradient?: boolean }
  > = {
    "gemini-2.5-flash-preview-05-20": { displayName: "gemini-2.5-flash" },
    "gemini-2.5-pro": {
      displayName: "gemini-2.5-pro",
      showMax: true,
      hasGradient: true,
    },
  };

  return modelMap[modelValue] || { displayName: modelValue };
};

// AutoAwesome icon component
const ModelIcon = ({ size = 16 }: { size?: number }) => (
  <AutoAwesome sx={{ fontSize: size }} color="primary" />
);

interface ModelSelectorProps {
  variant?: "default" | "header";
  disabled?: boolean;
}

export const ModelSelector: React.FC<ModelSelectorProps> = ({
  variant = "default",
  disabled = false,
}) => {
  const { selectedModel, setSelectedModel } = useStore((state) => ({
    selectedModel: state.selectedModel,
    setSelectedModel: state.setSelectedModel,
  }));

  // Check if viewport is macbook width or smaller (< 1700px)
  const isMacbookOrBelow = useIsBelow("fhdToMacbook");

  const [modelOptions, setModelOptions] = useState([
    ...initialModelOptions,
    { value: "add-custom-model", label: "Add custom model..." },
  ]);
  const [customModelInput, setCustomModelInput] = useState<string>("");
  const [isCustomModelDialogOpen, setIsCustomModelDialogOpen] =
    useState<boolean>(false);

  const handleOpenCustomModelDialog = () => {
    setIsCustomModelDialogOpen(true);
  };

  const handleCloseCustomModelDialog = () => {
    setIsCustomModelDialogOpen(false);
    setCustomModelInput("");
  };

  const handleAddCustomModel = () => {
    // Feature disabled - Coming Soon!
    return;
  };

  const handleModelChange = (event: SelectChangeEvent<string>) => {
    const value = event.target.value as string;
    if (value === "add-custom-model") {
      handleOpenCustomModelDialog();
    } else {
      setSelectedModel(value);
    }
  };

  const isHeaderVariant = variant === "header";

  // Generate tooltip text based on selected model
  const getTooltipText = () => {
    const modelInfo = getModelDisplayInfo(selectedModel);
    let baseText = "Choose AI mode.";
    if (modelInfo.hasGradient) {
      baseText += "\n\nGemini pro is 60% more accurate than other models";
    }
    return baseText;
  };

  return (
    <>
      <Tooltip title={getTooltipText()} arrow placement="right">
        <FormControl
          sx={{
            minWidth: 160,
            // Push left by 20vw on macbook width or smaller screens
            ...(isMacbookOrBelow && {
              transform: isHeaderVariant ? "translateX(-20vw)" : "",
            }),
          }}
          size="small"
        >
          <Select
            labelId="model-select-label"
            id="model-select"
            value={selectedModel}
            onChange={handleModelChange}
            disabled={disabled}
            displayEmpty
            renderValue={(selected) => {
              if (!selected) return "Select Model";
              const modelInfo = getModelDisplayInfo(selected);
              return (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: "100%",
                  }}
                >
                  <div style={{ marginLeft: "3px", marginBottom: "-3px" }}>
                    <ModelIcon size={16} />
                  </div>
                  <span
                    style={{
                      ...(modelInfo.hasGradient
                        ? {
                            fontSize: "13px",
                            fontWeight: 500,
                            background: `linear-gradient(135deg, ${theme.custom.lightestPink.replace(
                              "##",
                              "#"
                            )} 0%, ${theme.custom.pinkerPink} 100%)`,
                            WebkitBackgroundClip: "text",
                            WebkitTextFillColor: "transparent",
                            backgroundClip: "text",
                          }
                        : {
                            fontSize: "13px",
                            fontWeight: 500,
                            color: theme.custom.darkGrey,
                          }),
                      marginLeft: "6px",
                      flex: 1,
                    }}
                  >
                    {modelInfo.displayName}
                    {modelInfo.showMax && (
                      <span
                        style={
                          modelInfo.hasGradient
                            ? {
                                marginLeft: "4px",
                                fontSize: "11px",
                                fontWeight: 600,
                                background: `linear-gradient(135deg, ${theme.custom.lightestPink.replace(
                                  "##",
                                  "#"
                                )} 0%, ${theme.custom.pinkerPink} 100%)`,
                                WebkitBackgroundClip: "text",
                                WebkitTextFillColor: "transparent",
                                backgroundClip: "text",
                              }
                            : {
                                marginLeft: "4px",
                                fontSize: "11px",
                                fontWeight: 600,
                                color: theme.custom.darkGrey,
                              }
                        }
                      >
                        MAX
                      </span>
                    )}
                  </span>
                </div>
              );
            }}
            sx={{
              borderRadius: isHeaderVariant ? "0 0 20px 20px" : "20px",
              fontSize: "13px",
              fontWeight: 700,
              backgroundColor: "transparent",
              border: "1px solid #E5E7EB",
              ...(isHeaderVariant && {
                borderTop: "none",
                marginTop: "-1px",
              }),
              "& .MuiOutlinedInput-notchedOutline": {
                border: "none",
              },
              "&:hover": {
                backgroundColor: theme.custom.lightestGrey,
              },
              "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                border: "none",
              },
              "& .MuiSelect-select": {
                paddingTop: "6px",
                paddingBottom: "6px",
              },
              "& .MuiSelect-icon": {
                color: theme.custom.pink,
              },
            }}
            MenuProps={{
              PaperProps: {
                sx: {
                  borderRadius: "12px",
                  border: "1px solid #E5E7EB",
                  boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
                  "& .MuiMenuItem-root": {
                    fontSize: "13px",
                    padding: "6px 12px",
                    "&:hover": {
                      backgroundColor: theme.custom.lightestGrey,
                    },
                  },
                },
              },
            }}
          >
            {modelOptions.map((option) => {
              if (option.value === "add-custom-model") {
                return (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                );
              }

              const modelInfo = getModelDisplayInfo(option.value);
              return (
                <MenuItem key={option.value} value={option.value}>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "0px",
                      width: "100%",
                    }}
                  >
                    <span
                      style={
                        modelInfo.hasGradient
                          ? {
                              fontSize: "13px",
                              fontWeight: 500,
                              background: `linear-gradient(135deg, ${theme.custom.lightestPink.replace(
                                "##",
                                "#"
                              )} 0%, ${theme.custom.pinkerPink} 100%)`,
                              WebkitBackgroundClip: "text",
                              WebkitTextFillColor: "transparent",
                              backgroundClip: "text",
                            }
                          : {
                              fontSize: "13px",
                              fontWeight: 500,
                              color: theme.custom.darkGrey,
                            }
                      }
                    >
                      {modelInfo.displayName}
                      {modelInfo.showMax && (
                        <span
                          style={
                            modelInfo.hasGradient
                              ? {
                                  marginLeft: "4px",
                                  fontSize: "11px",
                                  fontWeight: 600,
                                  background: `linear-gradient(135deg, ${theme.custom.lightestPink.replace(
                                    "##",
                                    "#"
                                  )} 0%, ${theme.custom.pinkerPink} 100%)`,
                                  WebkitBackgroundClip: "text",
                                  WebkitTextFillColor: "transparent",
                                  backgroundClip: "text",
                                }
                              : {
                                  marginLeft: "4px",
                                  fontSize: "11px",
                                  fontWeight: 600,
                                  color: theme.custom.darkGrey,
                                }
                          }
                        >
                          MAX
                        </span>
                      )}
                    </span>
                  </div>
                </MenuItem>
              );
            })}
          </Select>
        </FormControl>
      </Tooltip>

      {/* Custom Model Dialog */}
      <Dialog
        open={isCustomModelDialogOpen}
        onClose={handleCloseCustomModelDialog}
      >
        <DialogTitle>Add Custom Model</DialogTitle>
        <DialogContent>
          <DialogContentText
            sx={{
              mb: 2,
              textAlign: "center",
              fontSize: "18px",
              fontWeight: "bold",
              color: theme.custom.pink,
            }}
          >
            Coming Soon!
          </DialogContentText>
          <DialogContentText sx={{ mb: 2, textAlign: "center" }}>
            Custom model support will be available in a future update.
          </DialogContentText>
          <TextField
            autoFocus
            margin="dense"
            id="custom-model-name"
            label="Custom Model Name"
            type="text"
            fullWidth
            variant="outlined"
            value={customModelInput}
            onChange={(e) => setCustomModelInput(e.target.value)}
            disabled={true}
            onKeyDown={(e) => {
              if (e.key === "Enter" && customModelInput.trim() !== "") {
                e.preventDefault();
                handleAddCustomModel();
              }
            }}
            sx={{
              "& .MuiInputBase-input.Mui-disabled": {
                WebkitTextFillColor: "#999",
              },
              "& .MuiOutlinedInput-root.Mui-disabled": {
                "& fieldset": {
                  borderColor: "#ddd",
                },
              },
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseCustomModelDialog}>Close</Button>
          <Button onClick={handleAddCustomModel} disabled={true}>
            Add & Use
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
